import { useQuery } from '@tanstack/react-query'
import { apiCall, internalClient } from '@/lib/api'
import { useAuth } from './AuthProvider'

export interface StaffMe {
  is_staff: boolean
  /**
   * A string array rather than the product's named booleans
   * (`MeResponse { is_staff, can_author, can_approve }`). This IS the write
   * seam: adding `calibration:write` later is a server change and zero client
   * refactor.
   */
  capabilities: string[]
  user_id: string | null
  display_name: string | null
  granted_at: string | null
}

/**
 * The ONE route that answers 200 to any valid realm token, with the same body
 * shape for everyone.
 *
 * It has to be answerable, or the SPA cannot tell "you are not staff" from "the
 * service is down" — and a legitimately-rejected user would get a red error
 * screen that looks like an escalatable bug.
 *
 * `retry: false` on purpose: this is an authorization answer, and retrying it
 * only delays the screen. `staleTime: Infinity` because staff membership does
 * not change mid-session, and re-asking on every focus would put an
 * authorization round trip in front of every tab switch.
 */
export function useStaffMe() {
  const { authenticated } = useAuth()

  return useQuery({
    queryKey: ['staff', 'me'],
    queryFn: () => apiCall<StaffMe>(internalClient, { method: 'GET', url: '/staff/me' }),
    retry: false,
    staleTime: Infinity,
    // ── THIS GUARD IS LOAD-BEARING, AND ITS ABSENCE WAS INVISIBLE ───────────
    // <StaffOnly> calls this hook unconditionally, so without `enabled` the
    // query fired on the FIRST render — before keycloak.init() had resolved and
    // while `keycloak.token` was still undefined. The request interceptor
    // attaches the header only `if (keycloak.token)`, so it went out bare and
    // came back 401 "missing bearer token".
    //
    // `retry: false` and `staleTime: Infinity` are both correct on their own —
    // this is an authorization answer, not data — but together they make that
    // first 401 PERMANENT. The query never re-asks, so authenticating a moment
    // later changes nothing, and StaffOnly's `staff.isError` branch shows
    // <NoAccessScreen> to a user who is, in fact, staff.
    //
    // Deliberately `authenticated` and not `ready`: ready only means init
    // finished, and an anonymous visitor is ready too.
    enabled: authenticated,
  })
}

/**
 * The ONLY authority on what this user may do — and it is the server's answer,
 * not a JWT claim read in the browser.
 *
 * The product SPA has `useRoles`/`hasPermission` because its customer app gates
 * on token roles, and it carries the `root:all` bypass rule inside
 * `hasPermission()`. Two copies of an authorization rule drift toward the
 * permissive one, so this app keeps exactly one. eslint blocks importing the
 * other shape.
 */
export function useCan(capability: string): boolean {
  const { data } = useStaffMe()
  return Boolean(data?.is_staff && data.capabilities.includes(capability))
}
